import { useState } from "react";
import { Navigation, ExternalLink, Clock, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

/* Reusable "Directions & Time to Destination" block.
   Given a start_address + destination_address, generates a route link on the
   user's chosen provider (Apple Maps or Google Maps) and lets them save an
   "average time" that persists on the segment for future reference. */

const encode = (s) => encodeURIComponent((s || "").trim());

function buildLink(provider, from, to) {
  if (!from || !to) return "";
  if (provider === "apple") {
    return `https://maps.apple.com/?saddr=${encode(from)}&daddr=${encode(to)}&dirflg=d`;
  }
  return `https://www.google.com/maps/dir/?api=1&origin=${encode(from)}&destination=${encode(to)}&travelmode=driving`;
}

export default function DirectionsField({
  startAddress, destAddress, provider, averageTime, routeLink,
  onChange,           // ({provider, average_time, route_link}) => void — persists to segment
  testPrefix = "dir",
}) {
  const [prov, setProv] = useState(provider || "google");
  const [time, setTime] = useState(averageTime || "");
  const [savingTime, setSavingTime] = useState(false);
  const link = routeLink || buildLink(prov, startAddress, destAddress);
  const disabled = !startAddress || !destAddress;

  const openMap = () => {
    if (!link) { toast.error("Enter both start and destination addresses first"); return; }
    onChange?.({ provider: prov, route_link: link, average_time: time });
    try { window.open(link, "_blank", "noopener,noreferrer"); }
    catch { toast.error("Could not open the map — copy the link below."); }
  };

  const commitTime = () => {
    setSavingTime(true);
    onChange?.({ provider: prov, route_link: link, average_time: time });
    setTimeout(() => setSavingTime(false), 500);
    toast.success("Average time saved for this route");
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-blue-50/40 p-3.5 space-y-3" data-testid={`${testPrefix}-field`}>
      <div className="flex items-center gap-1.5 text-[11px] font-800 uppercase tracking-widest text-blue-700">
        <Navigation className="w-3.5 h-3.5" /> Directions &amp; Time to Destination
      </div>
      <div className="text-[12px] text-slate-600 space-y-0.5">
        <div><span className="font-700 text-slate-500">Start:</span> {startAddress || <span className="italic text-slate-400">— enter start address above</span>}</div>
        <div><span className="font-700 text-slate-500">End:</span> {destAddress || <span className="italic text-slate-400">— enter destination address above</span>}</div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex border border-slate-300 rounded-full overflow-hidden" data-testid={`${testPrefix}-provider`}>
          {[["google", "Google Maps"], ["apple", "Apple Maps"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setProv(k)}
              data-testid={`${testPrefix}-provider-${k}`}
              className={`px-3 py-1 text-xs font-700 ${prov === k ? "bg-blue-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
              {l}
            </button>
          ))}
        </div>
        <button type="button" onClick={openMap} disabled={disabled}
          data-testid={`${testPrefix}-open`}
          className="inline-flex items-center gap-1.5 border border-blue-500 text-blue-700 bg-white text-xs font-800 px-3 py-1.5 rounded-full hover:bg-blue-50 disabled:opacity-40">
          <ExternalLink className="w-3.5 h-3.5" /> Open route
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
          <Clock className="w-3.5 h-3.5 text-slate-500" /> Average time
        </label>
        <input value={time} onChange={(e) => setTime(e.target.value)}
          placeholder="e.g. 45 min · 1h 20m"
          data-testid={`${testPrefix}-time`}
          className="flex-1 min-w-[140px] border border-slate-300 rounded px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-500" />
        <button type="button" onClick={commitTime} disabled={disabled}
          data-testid={`${testPrefix}-save-time`}
          className="inline-flex items-center gap-1 text-xs font-800 bg-emerald-600 text-white px-3 py-1.5 rounded-full hover:bg-emerald-700 disabled:opacity-40">
          {savingTime ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save time
        </button>
      </div>

      {link && (
        <div className="text-[10px] text-slate-400 font-mono truncate" title={link} data-testid={`${testPrefix}-link`}>
          {link}
        </div>
      )}
    </div>
  );
}
